import {ValidationResult, ValidationResultRecordType} from "@/shared/timr";
import {BulkSelectorContextMenu} from "@/shared/ui/bulkSelectorContextMenu";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {isAccessToRecordAllowed} from "@/shared/utils/misc";
import {formatDate, formatDateDay, formatTime, parseISODate, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {getHighestSeverity} from "@/shared/validations/validationUtil";

export interface ValidationsCollapseContent {
	validations: ValidationResult[];
	isAddWorkingTimeButtonActive: boolean; // for add working time context menu button
	isAddAbsenceButtonActive: boolean; // for add vacation context menu button
	validationTotalCount: number; // for showing a link to the validation page
}

/** `detail` payload of the EVENT_ADD_WORKING_TIME_CLICKED / EVENT_ADD_ABSENCE_CLICKED custom events. */
export interface ValidationAddEventDetail {
	validationId: number;
	userId: number;
	displayDate: string;
}

/** `detail` payload of the EVENT_RECORD_CLICKED / EVENT_RECORD_REF_CLICKED custom events. */
export interface ValidationRecordEventDetail {
	recordType: ValidationResultRecordType;
	id: number;
	link: JQuery;
}

const template = `
	<div class="alert mb-10 cursor-pointer collapse-component">
		<div class="overlay-processing" style="display: none">
			<div class="overlay-processing-container">
				<span class="overlay-processing-text">
					<i class="icon-spinner2 spinner text-grey-600"></i> ${messages["processingPanel.processing"]}
				</span>
			</div>
		</div>
		<div class="display-flex justify-content-space-between">
			<table>
				<tr>
					<td><i id="severity-icon" class="pr-10 position-left fa"></i></td>
					<td id="headline"></td>
				</tr>
			</table>
			<a data-toggle="collapse" aria-expanded="false" class="collapsed pull-right">
				<span data-toggle="show">${messages["timeAccount.validationProblems.showDetails"]}<i class="fa fa-angle-down ml-5"></i></span>
				<span data-toggle="hide" style="display: none">${messages["timeAccount.validationProblems.hideDetails"]}<i class="fa fa-angle-up ml-5"></i></span>
			</a>
		</div>
		<div class="cursor-default panel-collapse collapse" aria-expanded="false"></div>
	</div>
	`;

/**
 * Custom validations-collapse web component that includes a scrollable and collapsible list element for validations
 * The style of the collapse element is based on the validation with the highest severity
 * Implementable by html <validations-collapse></validations-collapse>, data can be updated by calling <code>set validations</code> on the web component
 * Can be selected via <code>document.getElementById(...) as ValidationsCollapseComponent</code>)
 * Triggered custom events are used to open the dialog from the outside of this component
 * Use attribute {@link headline} for title of collapse e.g. "The selected time range may contain errors or be incomplete"
 */
export class ValidationsCollapseComponent extends HTMLElement {
	public static EVENT_RECORD_CLICKED = "onRecordClicked";
	public static EVENT_RECORD_REF_CLICKED = "onRecordRefClicked";
	public static EVENT_ADD_WORKING_TIME_CLICKED = "onAddWorkingTimeClicked";
	public static EVENT_ADD_ABSENCE_CLICKED = "onAddAbsenceClicked";

	private loadingOverlay: JQuery;
	private _content: ValidationsCollapseContent;
	private collapse: JQuery;
	private show: JQuery;
	private hide: JQuery;

	private addTimeContextMenu: BulkSelectorContextMenu;

	/* DEVNOTE this component is not encapsulated (with shadow DOM), otherwise external CSS classes are not available here.
	 * Dynamic generation component without shadow DOM, is only available if component is connected, therefore init is done inside the connected callback
	 * https://html.spec.whatwg.org/multipage/custom-elements.html#custom-element-conformance
	 */
	private connectedCallback() {
		this.innerHTML = template;

		// set headline
		$(this).find("#headline").text(this.headline);

		this.collapse = $(this).find(".collapse");
		this.show = $(this).find("span[data-toggle='show']");
		this.hide = $(this).find("span[data-toggle='hide']");
		this.loadingOverlay = $(this).find("div.overlay-processing");

		// jQuery collapse listeners
		this.collapse.on("show.bs.collapse", this.onShowCollapse);
		this.collapse.on("hide.bs.collapse", this.onHideCollapse);

		// listeners for clicks on referenced record
		this.collapse.on("click", "a[data-target='record'][data-id]", e => this.onRecordClicked(ValidationsCollapseComponent.EVENT_RECORD_CLICKED, e));
		this.collapse.on("click", "a[data-target='recordRef'][data-id]", e => this.onRecordClicked(ValidationsCollapseComponent.EVENT_RECORD_REF_CLICKED, e));
		this.collapse.on("click", "a[data-user-trust-based]", () => BootstrapModal.trustBasedNoAccess());

		// clicks to open collapse
		$(this).click(this.onContainerClick);
	}

	public toggleLoadingOverlay(show: boolean) {
		this.loadingOverlay.toggle(show);
	}

	/**
	 * Resets content/data of the validations collapse component
	 */
	public reset() {
		this.content = {
			validations: [],
			isAddWorkingTimeButtonActive: false,
			isAddAbsenceButtonActive: false,
			validationTotalCount: 0
		};
	}

	set content(content: ValidationsCollapseContent) {
		this._content = content;
		this.updateData();
		this.updateDropdown();
	}

	get content() {
		return this._content;
	}

	get validations() {
		return this.content.validations;
	}

	private updateData() {
		// DEVNOTE create context menu at this point for a correct active state of the menu buttons by isAddWorkingTimeButtonActive/isaddAbsenceButtonActive (TIMR-8474)
		this.addTimeContextMenu = this.createAddTimeContextMenu();

		const validationsAvailable = this.validations.length > 0;

		// hide custom element if no validations are fetched
		$(this).toggle(validationsAvailable);

		// append data if available
		if (validationsAvailable) {
			this.collapse
				.empty()
				.append(this.formatValidationResults(this.validations, globals.isAuthorizedForAllUsers || globals.isGroupLeader));

			// set container style to highest severity
			$(this).find(".alert")
				.removeClass((i, className) => (className.match(/(^|\s)alert-validation-severity-\S*/g) || []).join(""))
				.addClass("alert-validation-severity-" + getHighestSeverity(this.validations));

			// set severity icon to highest severity
			$(this).find("#severity-icon")
				.removeClass((i, className) => (className.match(/(^|\s)fa-validation-severity-\S*/g) || []).join(""))
				.addClass("fa-validation-severity-" + getHighestSeverity(this.validations));
		}
		this.toggleLoadingOverlay(false);
	}

	private updateDropdown() {
		this.addTimeContextMenu.registerOnButton($(this).find(".addTimeDropdown"), true, true);
		this.showDropdownOnBody();
	}

	private onRecordClicked(eventName: string, e: JQuery.TriggeredEvent) {
		// get clicked <a> element
		const a = $(e.currentTarget as HTMLElement);

		// trigger event for opening the dialog outside
		this.dispatchEvent(
			new CustomEvent<ValidationRecordEventDetail>(eventName, {
				detail: {
					id: Number(a.attr("data-id")),
					recordType: a.attr("data-record-type") as ValidationResultRecordType,
					link: a
				}
			})
		);
	}

	private readonly onContainerClick = (e: JQuery.TriggeredEvent) => {
		// if container is clicked anywhere except the results table collapse opens/closes
		if ($(e.target as HTMLElement).closest(".recording-validation-container").length === 0) {
			this.collapse.collapse("toggle");
		}
	}

	private readonly onShowCollapse = () => {
		this.show.hide();
		this.hide.show();
	}

	private readonly onHideCollapse = () => {
		this.show.show();
		this.hide.hide();
	}

	/**
	 * Renders the individual validations into a table view
	 * If showUser is active the user is displayed in a column
	 * @param validationResults list of validation results which are an object to display RecordingValidation
	 * @param showUser if active, user column will be shown
	 */
	private formatValidationResults(validationResults: ValidationResult[], showUser: boolean = false) {
		const validationContainer = $("<div>").addClass("recording-validation-container");
		const table = $("<table>").addClass("mt-10");
		for (const validation of validationResults) {
			const {
				day,
				user,
				messageCode,
				messageArguments,
				recordedEntry,
				recordedEntryRef,
				status,
				severity
			} = validation;

			// create table row element and append weekday prefix + date
			const date = parseISODate(day);
			const tr = $("<tr>")
				.append($("<td>").html(`<i class="fa fa-validation-severity-${severity} position-left" title="${messages["recordingValidation.severity." + severity]}"></i>`))
				.append($("<td>").text(formatDateDay(date)))
				.append($("<td>").text(formatDate(date)));

			if (showUser) {
				tr.append($("<td>").text(user.fullname));
			}

			// show status if persistent validations are enabled e.g. "OPEN"
			if (globals.savedValidationsEnabled) {
				tr.append($("<td>").text(messages["recordingValidation.status." + status]));
			}

			// attach message e.g. "No working time recorded on a working day"
			let td: JQuery;
			if (validation.messageCode === "validationResult.missingEntryOnWorkingDay" || validation.messageCode === "validationResult.missingEntryOnHalfDayHoliday") {
				td = $("<td>").html(this.formatMissingWorkingTimeDropdown(validation));
			} else {
				td = $("<td>").text(messages.withArguments(messageCode, ...messageArguments ?? []));
			}

			// attach the referenced records if available e.g. "Project time without working time {recordEntry} -> {recordEntryRef}" which can be opened in a modal
			if (recordedEntry != null && recordedEntryRef != null) {
				// add fallback for type due changes between mappings of runtime and persisted validations
				// todo: remove this with TIMR-7677
				const recordType = recordedEntry.type ?? (recordedEntry.task === undefined ? "workingTime" : "projectTime");
				const refRecordType = recordedEntryRef.type ?? (recordedEntryRef.task === undefined ? "workingTime" : "projectTime");
				const recordStart = parseISOZonedDateTime(recordedEntry.start);
				const recordEnd = parseISOZonedDateTime(recordedEntry.end);
				const refRecordStart = parseISOZonedDateTime(recordedEntryRef.start);
				const refRecordEnd = parseISOZonedDateTime(recordedEntryRef.end);

				// time zone should be visible if the client setting is true or if start and end time zone are different of all referenced records
				const showTimeZone = globals.showTimeZone || ![recordStart.offset, recordEnd.offset, refRecordStart.offset, refRecordEnd.offset].every((val, i, arr) => val === arr[0]);

				td.append(`<a data-target="record" data-record-type="${recordType}" ${isAccessToRecordAllowed(recordedEntry) ? `data-id="${recordedEntry.id}"` : "data-user-trust-based"}>
										${formatTime(recordStart)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${recordStart.offset}</span>
										<i class="time-separation-symbol ml-5 mr-5"></i>
										${formatTime(recordEnd)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${recordEnd.offset}</span>
									</a>
									${messages["timeAccount.validationProblems.and"]}
									<a data-target="recordRef" data-record-type="${refRecordType}" ${isAccessToRecordAllowed(recordedEntry) ? `data-id="${recordedEntryRef.id}"` : "data-user-trust-based"}>
										${formatTime(refRecordStart)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${refRecordStart.offset}</span>
										<i class="time-separation-symbol ml-5 mr-5"></i>
										${formatTime(refRecordEnd)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${refRecordEnd.offset}</span>
									</a>`);
			} else if (recordedEntry != null) {
				// add fallback for type due changes between mappings of runtime and persisted validations (becomes obsolete with TIMR-7677)
				// todo: remove this with TIMR-7677
				const recordType = recordedEntry.type ?? (recordedEntry.task === undefined ? "workingTime" : "projectTime");
				const recordStart = parseISOZonedDateTime(recordedEntry.start);
				const recordEnd = parseISOZonedDateTime(recordedEntry.end);

				// time zone should be visible if the client setting is true or if start and end time zone are different
				const showTimeZone = globals.showTimeZone || recordStart.offset !== recordEnd.offset;

				td.append(`<a data-target="record" data-record-type="${recordType}" ${isAccessToRecordAllowed(recordedEntry) ? `data-id="${recordedEntry.id}"` : "data-user-trust-based"}>
										${formatTime(recordStart)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${recordStart.offset}</span>
										<i class="time-separation-symbol ml-5 mr-5"></i>
										${formatTime(recordEnd)}
										<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${recordEnd.offset}</span>
									</a>`);
			}
			tr.append(td);
			table.append(tr);
		}
		validationContainer.append(table);

		// append link referring to recording validations page
		if (globals.savedValidationsEnabled) {
			const totalValidationsCount = this.content.validationTotalCount;
			validationContainer.append($("<a>")
				.addClass("display-inline-block mt-10")
				.attr("href", "/timr/reports/recordingValidations.html")
				.text(totalValidationsCount && totalValidationsCount > validationResults.length ?
					messages.withArguments("timeAccount.validationsContainer.showAll.count", totalValidationsCount) :
					messages["timeAccount.validationsContainer.showAll"]
				));
		}

		return validationContainer;
	}

	private formatMissingWorkingTimeDropdown(validation: ValidationResult): string {
		return $("<div>").addClass("display-flex")
			.append($("<span>").text(messages.withArguments(validation.messageCode, ...validation.messageArguments ?? [])).addClass("display-flex mr-20"))
			.append($("<ul>").addClass("list-context-menu")
				.append($("<li>").addClass("dropdown")
					.append($("<a>",
						{
							"data-toggle": "dropdown",
							"aria-expanded": "false",
							"data-id": validation.id,
							"data-date": validation.day,
							"data-userid": validation.user.id
						}
					).addClass("dropdown-toggle").addClass("addTimeDropdown")
						.append($("<i>").addClass("far fa-plus position-left"))
						.append($("<span>").text(messages["recordingValidations.add"])))
					.append(this.addTimeContextMenu.render())
				)
			).prop("outerHTML") as string;
	}

	private createAddTimeContextMenu(): BulkSelectorContextMenu {
		return new BulkSelectorContextMenu()
			.addMenuEntry({
				label: messages["recordingValidations.context.addWorkingTime"],
				active: this.content.isAddWorkingTimeButtonActive,
				action: (e, contextElement) => {
					const menu = contextElement.closest(".dropdown-menu");
					const validationId = menu.data("id") as number;
					const userId = menu.data("userid") as number;
					const displayDate = menu.data("date") as string;
					this.dispatchEvent(new CustomEvent<ValidationAddEventDetail>(ValidationsCollapseComponent.EVENT_ADD_WORKING_TIME_CLICKED, {
						detail: {
							validationId: validationId,
							userId: userId,
							displayDate: displayDate
						}
					}));
				}
			})
			.addMenuEntry({
				label: messages["recordingValidations.context.addAbsence"],
				active: this.content.isAddAbsenceButtonActive,
				action: (e, contextElement) => {
					const menu = contextElement.closest(".dropdown-menu");
					const validationId = menu.data("id") as number;
					const userId = menu.data("userid") as number;
					const displayDate = menu.data("date") as string;
					this.dispatchEvent(new CustomEvent<ValidationAddEventDetail>(ValidationsCollapseComponent.EVENT_ADD_ABSENCE_CLICKED, {
						detail: {
							validationId: validationId,
							userId: userId,
							displayDate: displayDate
						}
					}));
				}
			});
	}

	get headline() {
		return this.getAttribute("headline");
	}

	static get observedAttributes() {
		return ["headline"];
	}

	private showDropdownOnBody() {
		// hold onto the drop down menu
		let dropdownMenu: JQuery;

		// and when you show it, move it to the body
		$(this).find(".dropdown").on("show.bs.dropdown", function (e) {

			// grab the menu
			dropdownMenu = $(e.target).find(".dropdown-menu");

			const link = $(e.target).closest(".dropdown").find("a:first");
			dropdownMenu.data("id", link.data("id") as string);
			dropdownMenu.data("userid", link.data("userid") as string);
			dropdownMenu.data("date", link.data("date") as string);

			// detach it and append it to the body
			$("body").append(dropdownMenu.detach());

			// grab the new offset position
			const eOffset = $(e.target).offset();

			// make sure to place it where it would normally go (this could be improved)
			dropdownMenu.css({
				"display": "block",
				"top": eOffset.top + $(e.target).outerHeight(),
				"left": eOffset.left
			});
		});

		$(".recording-validation-container").on("scroll", () => {
			if (dropdownMenu) {
				// On scroll close the dropdown, only if available
				$(this).find(".dropdown").triggerHandler("hide.bs.dropdown");
			}
		});

		// and when you hide it, reattach the drop down, and hide it normally
		$(this).find(".dropdown").on("hide.bs.dropdown", function (e) {
			$(e.target).append(dropdownMenu?.detach());
			dropdownMenu?.hide();
		});
	}
}
